// ── Event Handlers ──────────────────────────────────────────────────
// Mouse/keyboard event handlers, drag & drop, mode-specific click handlers

import {
  state, clearSelection, saveState, commitHistory, undo, redo, copySelected, pasteClipboard,
  toggleMultiSelection, clearMultiSelection, deleteMultiSelection,
  createSnapshot,
  type MultiSelectType, type StateTree, type Snapshot,
  createSprinkler,
} from './state.ts';
import {
  $, svg, container,
  screenToMeters, snapMeters, metersToSvg,
  svgEl, clearLayer, updateViewBox,
  smartGuideLayer, tempLayer,
  snapToGuides, findSnapTarget,
  zoomAt, zoomToFit,
} from './canvas.ts';
import { showToast } from '../shared/utils.ts';
import { getArcData, isPointInSprinklerArc, generateAutoComponents, getDripTypes, WATER_SOURCE_DEFAULTS } from '../calculations/logic.ts';
import { getWeatherStations } from '../catalog/catalog-loader.ts';
import { LANDSCAPE_OBJECTS } from '../catalog/catalog-data.ts';
import { t } from '../shared/i18n.ts';
import { convertLength, convertPrecipRate, getUnitLabels } from '../shared/units.ts';
import {
  autoAssignSprinklersToZones,
  renderTemp, renderCoverage, renderSprinklers, renderPipes, renderDripLines,
  renderBoundary, renderTrees, renderZones, renderValveObjects, renderWeatherStations,
  renderSmartGuides,
  updateStats, updatePropertiesPanel, updatePipePropertiesPanel,
  updateDripPropertiesPanel, updateTreePropertiesPanel,
  updateValveObjPropertiesPanel, updateWeatherStationPropertiesPanel,
} from './render.ts';
import { setMode } from './toolbar.ts';

/** Total number of placeable elements — used to detect successful paste/duplicate */
function elementCount(): number {
  return state.sprinklers.length + state.pipes.length + state.dripLines.length
    + state.trees.length + state.valveObjects.length + state.weatherStations.length
    + state.zones.length;
}

// ── Callbacks set by app.js after initialization ──────────────────
interface EventCallbacks {
  renderAll: () => void;
  selectSprinkler: (id: number) => void;
  selectTree: (id: number) => void;
  selectValveObj: (id: number) => void;
  selectWeatherStation: (id: number) => void;
}
let callbacks = {} as EventCallbacks;

export function setEventCallbacks(cbs: EventCallbacks) {
  callbacks = cbs;
}

function renderAll() { callbacks.renderAll(); }
function selectSprinkler(id: number) { callbacks.selectSprinkler(id); }
function selectTree(id: number) { callbacks.selectTree(id); }
function selectValveObj(id: number) { callbacks.selectValveObj(id); }
function selectWeatherStation(id: number) { callbacks.selectWeatherStation(id); }

// ── Pan ──────────────────────────────────────────────────────────────
function startPan(e: Event) {
  state.isPanning = true;
  state.panStart = { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
  container().classList.add("is-panning");
}

// ── Canvas Events ───────────────────────────────────────────────────
export function onCanvasMouseDown(e: MouseEvent) {
  // Middle button — always pan
  if (e.button === 1) {
    e.preventDefault();
    startPan(e);
    return;
  }
  if (e.button !== 0) return;

  // Space + left click — pan
  if (state.spaceHeld) {
    e.preventDefault();
    startPan(e);
    return;
  }

  const snapped = snapMeters(screenToMeters(e.clientX, e.clientY));

  switch (state.mode) {
    case "select":    handleSelectDown(e.target as SVGElement, snapped, e); break;
    case "boundary":  handleBoundaryClick(snapped, e); break;
    case "sprinkler": handleSprinklerClick(snapped); break;
    case "zone":      handleZoneClick(snapped, e); break;
    case "pipe":      handlePipeClick(snapped, e); break;
    case "drip":      handleDripClick(snapped, e); break;
    case "tree":      handleTreeClick(snapped); break;
    case "valveObj":  handleValveObjClick(snapped); break;
    case "weatherStation": handleWeatherStationClick(snapped); break;
    case "delete":    handleDeleteClick(e.target as SVGElement); break;
  }
}

export function onCanvasMouseMove(e: MouseEvent) {
  const snapped = snapMeters(screenToMeters(e.clientX, e.clientY));

  // Status bar: coordinates + line length while drawing
  const units = state.userUnits, unitLabels = getUnitLabels(units);
  const len = (m: number, digits: number) => convertLength(m, units).toFixed(digits);
  let coordsText = t("status.coords", { x: len(snapped.x, 1), y: len(snapped.y, 1), unit: unitLabels.length });
  const drawingPts = state.mode === "boundary" ? state.tempBoundary
                   : state.mode === "zone" ? state.tempZone
                   : state.mode === "pipe" ? state.tempPipe
                   : state.mode === "drip" ? state.tempDripLine : null;
  if (drawingPts && drawingPts.length > 0) {
    const last = drawingPts[drawingPts.length - 1];
    const segLen = Math.hypot(snapped.x - last.x, snapped.y - last.y);
    let totalLen = segLen;
    for (let i = 1; i < drawingPts.length; i++) {
      totalLen += Math.hypot(drawingPts[i].x - drawingPts[i - 1].x, drawingPts[i].y - drawingPts[i - 1].y);
    }
    coordsText += ` | ${t("status.segment", { len: len(segLen, 2), unit: unitLabels.length })} | ${t("status.totalLength", { len: len(totalLen, 2), unit: unitLabels.length })}`;
  }
  $("#statusCoords").textContent = coordsText;

  // Show precipitation intensity at cursor when heatmap is active
  if (state.coverageView === 'heatmap' && state.sprinklers.length > 0) {
    let totalPrecip = 0;
    for (const sp of state.sprinklers) {
      const arcData = getArcData(sp.model, sp.arc);
      if (isPointInSprinklerArc({
        px: snapped.x,
        py: snapped.y,
        sprinkler: { x: sp.x, y: sp.y, radius: sp.radius ?? 0, arc: sp.arc, rotation: sp.rotation }
      })) {
        totalPrecip += arcData.precip || 0;
      }
    }
    const precip = convertPrecipRate(totalPrecip, units).toFixed(units === "imperial" ? 2 : 1);
    $("#statusPrecip").textContent = t("status.precipitation", { value: precip, unit: unitLabels.precipRate });
    $("#statusPrecip").hidden = false;
  } else {
    $("#statusPrecip").hidden = true;
  }

  if (state.isPanning) {
    const ctm = svg().getScreenCTM();
    if (ctm) {
      state.viewBox.x -= (e.clientX - state.panStart.x) / ctm.a;
      state.viewBox.y -= (e.clientY - state.panStart.y) / ctm.d;
    }
    state.panStart = { x: e.clientX, y: e.clientY };
    updateViewBox();
    return;
  }

  if (state.isDragging) { handleDrag(snapped); return; }

  // Pipe temp preview
  if (state.mode === "pipe" && state.tempPipe.length > 0) {
    renderTemp();
    const lastPt = state.tempPipe[state.tempPipe.length - 1];
    const lastSvg = metersToSvg(lastPt.x, lastPt.y);
    const curSvg = metersToSvg(snapped.x, snapped.y);
    tempLayer().appendChild(svgEl("line", {
      x1: lastSvg.x, y1: lastSvg.y, x2: curSvg.x, y2: curSvg.y,
      stroke: "#546e7a", "stroke-width": 2, "stroke-dasharray": "6 3",
    }));
    // Snap indicator
    const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
    const snap = findSnapTarget(snapped, snapThreshold);
    if (snap) {
      const snapSvg = metersToSvg(snap.x, snap.y);
      tempLayer().appendChild(svgEl("circle", {
        cx: snapSvg.x, cy: snapSvg.y, r: 12, class: "pipe-snap-indicator",
      }));
    }
  }

  // Drip temp preview
  if (state.mode === "drip" && state.tempDripLine.length > 0) {
    renderTemp();
    const lastPt = state.tempDripLine[state.tempDripLine.length - 1];
    const lastSvg = metersToSvg(lastPt.x, lastPt.y);
    const curSvg = metersToSvg(snapped.x, snapped.y);
    const dripType = ((getDripTypes() || {}) as Record<string, Record<string, unknown>>)[state.activeDripModel] || {};
    tempLayer().appendChild(svgEl("line", {
      x1: lastSvg.x, y1: lastSvg.y, x2: curSvg.x, y2: curSvg.y,
      stroke: dripType.color || "#795548", "stroke-width": 2.5, "stroke-dasharray": "6 4",
    }));
  }

  // Preview lines while drawing
  if ((state.mode === "boundary" && state.tempBoundary.length > 0) ||
      (state.mode === "zone" && state.tempZone.length > 0)) {
    renderTemp();
    const pts = state.mode === "boundary" ? state.tempBoundary : state.tempZone;
    const lastPt = pts[pts.length - 1];
    const lastSvg = metersToSvg(lastPt.x, lastPt.y);
    const curSvg = metersToSvg(snapped.x, snapped.y);
    const color = state.mode === "boundary" ? "#2e7d32" : "#9c27b0";
    tempLayer().appendChild(svgEl("line", {
      x1: lastSvg.x, y1: lastSvg.y, x2: curSvg.x, y2: curSvg.y,
      stroke: color, "stroke-width": 1.5, "stroke-dasharray": "6 3",
    }));
  }

}

export function onCanvasMouseUp() {
  // Paired with `.is-panning` set on pan-start. `.is-pannable` (if space is
  // still held) survives and takes the cursor back to `grab`.
  if (state.isPanning) { state.isPanning = false; container().classList.remove("is-panning"); return; }
  if (state.isDragging) {
    // Remove .dragging class from all draggable SVG elements
    svg()?.querySelectorAll(".dragging").forEach((el: Element) => el.classList.remove("dragging"));
    // Commit the pre-drag snapshot to history ONLY when a real movement
    // occurred. Clicks that never produced a handleDrag() call leave
    // dragTargetMoved=false and the snapshot is discarded — so the undo
    // stack only contains meaningful steps.
    if (dragTargetMoved && preDragSnapshot) {
      // `commitHistory`, not a direct push: it also runs the first-mutation
      // hook that bootstraps a cloud draft. Pushing straight onto
      // `state.history` meant a session spent only dragging never created one.
      commitHistory(preDragSnapshot);
    }
    dragTargetMoved = false;
    preDragSnapshot = null;
    multiDragEntries = [];
    multiDragAnchor = null;
    state.isDragging = false;
    state.dragTarget = null;
    clearLayer(smartGuideLayer());
    autoAssignSprinklersToZones();
    renderAll();
  }
}

export function onCanvasWheel(e: WheelEvent) {
  e.preventDefault();
  zoomAt(e.clientX, e.clientY, e.deltaY > 0 ? 1.04 : 0.96);
}

// ── Mode Handlers ──────────────────────────────────────────────────
/**
 * Try to handle Shift+click for multi-selection.
 * Returns true if handled — the caller should early-return.
 */
function tryMultiSelectClick(target: SVGElement, e: Event): boolean {
  const me = e as MouseEvent;
  if (!me.shiftKey) return false;

  const pairs: Array<[string, MultiSelectType]> = [
    [".sprinkler-marker", "sprinkler"],
    [".tree-crown", "tree"],
    [".valve-obj-marker", "valveObj"],
    [".weather-station-marker", "weatherStation"],
    [".pipe-polyline", "pipe"],
    [".zone-polygon", "zone"],
    ["[data-type='dripLine']", "dripLine"],
  ];

  for (const pair of pairs) {
    const [selector, type]: [string, MultiSelectType] = pair;
    const el = target.closest(selector);
    if (!el) continue;
    const idAttr = (el as HTMLElement).dataset.id
      || (el as HTMLElement).dataset.zoneId
      || '0';
    const id = parseInt(idAttr, 10);
    if (!Number.isFinite(id) || id <= 0) continue;
    toggleMultiSelection(type, id);
    renderAll();
    return true;
  }
  return false;
}

// Per-drag state used to only record an undo snapshot when the user actually
// moved something. Without this, every mousedown (even a click that never
// dragged) pushed a duplicate snapshot and cleared the redo stack, making
// undo/redo feel broken. See bug 2/4.
let dragTargetMoved = false;
// Pre-drag snapshot captured on mousedown. Committed to history in
// onCanvasMouseUp only if the user actually moved the target, so undo
// restores the position from BEFORE the drag (not after).
let preDragSnapshot: Snapshot | null = null;

// ── Multi-drag bookkeeping ────────────────────────────────────────
// When the user mousedowns on an item that's part of the multi-selection
// set, we capture each selected item's starting position + the anchor's
// starting position so handleDrag can translate every peer by the same
// delta. `multiDragAnchor` is the {x,y} coordinate of the dragTarget at
// mousedown; handleDrag receives the new cursor position (snapped) and
// computes (snapped - multiDragAnchor) as the delta to apply.
interface MultiDragEntry { type: MultiSelectType; id: number; startX: number; startY: number; }
let multiDragEntries: MultiDragEntry[] = [];
let multiDragAnchor: { x: number; y: number } | null = null;

/** Capture the pre-drag snapshot + reset the moved flag. Called from every
 *  draggable branch of handleSelectDown. */
function beginDrag() {
  dragTargetMoved = false;
  preDragSnapshot = createSnapshot();
  multiDragEntries = [];
  multiDragAnchor = null;
}

/** Populate multiDragEntries/multiDragAnchor when the user starts dragging
 *  an element that's part of the active multi-selection. If the clicked
 *  element is NOT in the set (i.e. user started a fresh drag), leave both
 *  empty so handleDrag falls through to the single-target path. */
function beginMultiDragIfEligible(type: MultiSelectType, id: number) {
  if (state.multiSelection.size < 2) return;
  if (!state.multiSelection.has(`${type}:${id}`)) return;

  // Capture starting positions of every point-like element in the selection.
  // Vertex/zone drags are NOT included because "relative position" isn't
  // well-defined for a polyline vertex inside a separate element.
  const entries: MultiDragEntry[] = [];
  state.multiSelection.forEach((key) => {
    const sep = key.indexOf(':');
    const t = key.slice(0, sep) as MultiSelectType;
    const entryId = Number(key.slice(sep + 1));
    let start: { x: number; y: number } | null = null;
    if (t === 'sprinkler') {
      const sp = state.sprinklers.find((s) => s.id === entryId);
      if (sp) start = { x: sp.x, y: sp.y };
    } else if (t === 'tree') {
      const tr = state.trees.find((x) => x.id === entryId);
      if (tr) start = { x: tr.x, y: tr.y };
    } else if (t === 'valveObj') {
      const vo = state.valveObjects.find((x) => x.id === entryId);
      if (vo) start = { x: vo.x, y: vo.y };
    } else if (t === 'weatherStation') {
      const ws = state.weatherStations.find((x) => x.id === entryId);
      if (ws) start = { x: ws.x, y: ws.y };
    }
    if (start) entries.push({ type: t, id: entryId, startX: start.x, startY: start.y });
  });

  // Use the clicked item as the anchor — its starting (x,y) is the origin
  // the cursor delta is measured from.
  const anchor = entries.find((e) => e.type === type && e.id === id);
  if (!anchor) return;
  multiDragEntries = entries;
  multiDragAnchor = { x: anchor.startX, y: anchor.startY };
}

/** Apply the current cursor delta (snapped - multiDragAnchor) to every
 *  entry in multiDragEntries. Returns true if multi-drag handled the
 *  movement; caller should skip the single-target branch. */
function applyMultiDrag(snapped: { x: number; y: number }): boolean {
  if (!multiDragAnchor || multiDragEntries.length === 0) return false;
  const dx = snapped.x - multiDragAnchor.x;
  const dy = snapped.y - multiDragAnchor.y;
  for (const e of multiDragEntries) {
    const nx = e.startX + dx;
    const ny = e.startY + dy;
    if (e.type === 'sprinkler') {
      const sp = state.sprinklers.find((s) => s.id === e.id);
      if (sp) { sp.x = nx; sp.y = ny; }
    } else if (e.type === 'tree') {
      const tr = state.trees.find((x) => x.id === e.id);
      if (tr) { tr.x = nx; tr.y = ny; }
    } else if (e.type === 'valveObj') {
      const vo = state.valveObjects.find((x) => x.id === e.id);
      if (vo) { vo.x = nx; vo.y = ny; }
    } else if (e.type === 'weatherStation') {
      const ws = state.weatherStations.find((x) => x.id === e.id);
      if (ws) { ws.x = nx; ws.y = ny; }
    }
  }
  return true;
}

function handleSelectDown(target: SVGElement, _snapped: { x: number; y: number }, e: Event) {
  // Multi-select takes priority over single-select when shift is held
  if (tryMultiSelectClick(target, e)) return;

  // NOTE: saveState() was previously called here in every branch — moved to
  // onCanvasMouseUp, gated on `dragTargetMoved`, so clicks without drags
  // don't spam the undo stack.
  const marker = target.closest(".sprinkler-marker");
  if (marker) {
    const sid = parseInt((marker as HTMLElement).dataset.id!);
    // Preserve multi-selection when the user starts dragging an item
    // that's part of the current multi-selection; otherwise selectSprinkler
    // clears the set and the subsequent handleDrag only moves one item.
    const keepMulti = state.multiSelection.has(`sprinkler:${sid}`) && state.multiSelection.size > 1;
    if (!keepMulti) selectSprinkler(sid);
    state.isDragging = true;
    state.dragTarget = sid;
    state.dragType = "sprinkler";
    beginDrag();
    beginMultiDragIfEligible("sprinkler", sid);
    marker.classList.add("dragging");
    return;
  }
  // Click on tree crown
  const treeCrown = target.closest(".tree-crown");
  if (treeCrown) {
    const tid = parseInt((treeCrown as HTMLElement).dataset.id!);
    const keepMulti = state.multiSelection.has(`tree:${tid}`) && state.multiSelection.size > 1;
    if (!keepMulti) selectTree(tid);
    state.isDragging = true;
    state.dragTarget = tid;
    state.dragType = "tree";
    beginDrag();
    beginMultiDragIfEligible("tree", tid);
    treeCrown.classList.add("dragging");
    return;
  }
  // Click on valve object
  const valveMarker = target.closest(".valve-obj-marker");
  if (valveMarker) {
    const vid = parseInt((valveMarker as HTMLElement).dataset.id!);
    const keepMulti = state.multiSelection.has(`valveObj:${vid}`) && state.multiSelection.size > 1;
    if (!keepMulti) selectValveObj(vid);
    state.isDragging = true;
    state.dragTarget = vid;
    state.dragType = "valveObj";
    beginDrag();
    beginMultiDragIfEligible("valveObj", vid);
    valveMarker.classList.add("dragging");
    return;
  }
  const wsMarker = target.closest(".weather-station-marker");
  if (wsMarker) {
    const wid = parseInt((wsMarker as HTMLElement).dataset.id!);
    const keepMulti = state.multiSelection.has(`weatherStation:${wid}`) && state.multiSelection.size > 1;
    if (!keepMulti) selectWeatherStation(wid);
    state.isDragging = true;
    state.dragTarget = wid;
    state.dragType = "weatherStation";
    beginDrag();
    beginMultiDragIfEligible("weatherStation", wid);
    wsMarker.classList.add("dragging");
    return;
  }
  const bVertex = target.closest(".boundary-vertex");
  if (bVertex) {
    state.isDragging = true; state.dragType = "boundary-vertex";
    state.dragIndex = parseInt((bVertex as HTMLElement).dataset.index!);
    beginDrag();
    return;
  }
  const zVertex = target.closest(".zone-vertex");
  if (zVertex) {
    state.isDragging = true; state.dragType = "zone-vertex";
    state.dragTarget = parseInt((zVertex as HTMLElement).dataset.zoneId!);
    state.dragIndex = parseInt((zVertex as HTMLElement).dataset.index!);
    beginDrag();
    return;
  }
  const pipeVertex = target.closest(".pipe-vertex");
  if (pipeVertex) {
    const pipeId = parseInt((pipeVertex as HTMLElement).dataset.pipeId!);
    clearSelection();
    state.selectedPipe = pipeId;
    state.isDragging = true;
    state.dragType = "pipe-vertex";
    state.dragTarget = pipeId;
    state.dragIndex = parseInt((pipeVertex as HTMLElement).dataset.index!);
    beginDrag();
    renderAll();
    return;
  }

  const dripVertex = target.closest("[data-type='dripLine-vertex']");
  if (dripVertex) {
    const dripId = parseInt((dripVertex as HTMLElement).dataset.dripId!);
    clearSelection();
    state.selectedDripLine = dripId;
    state.isDragging = true;
    state.dragType = "dripLine-vertex";
    state.dragTarget = dripId;
    state.dragIndex = parseInt((dripVertex as HTMLElement).dataset.index!);
    beginDrag();
    renderAll();
    return;
  }

  const dripLine = target.closest("[data-type='dripLine']");
  if (dripLine) {
    clearSelection();
    state.selectedDripLine = parseInt((dripLine as HTMLElement).dataset.id!);
    renderAll();
    return;
  }

  const pipeLine = target.closest(".pipe-polyline");
  if (pipeLine) {
    clearSelection();
    // @ts-expect-error — TS strict
    state.selectedPipe = parseInt(pipeLine.dataset.id);
    renderAll();
    return;
  }

  const zonePoly = target.closest(".zone-polygon");
  if (zonePoly) {
    clearSelection();
    // @ts-expect-error — TS strict
    state.selectedZone = parseInt(zonePoly.dataset.zoneId);
    renderAll();
    return;
  }

  clearSelection();
  startPan(e);
  renderAll();
}

function handleBoundaryClick(snapped: { x: number; y: number }, e: MouseEvent) {
  if (e.detail === 2 && state.tempBoundary.length >= 3) { finishBoundary(); return; }
  if (state.tempBoundary.length >= 3) {
    const first = state.tempBoundary[0];
    if (Math.hypot(snapped.x - first.x, snapped.y - first.y) < state.gridSize * 0.5) {
      finishBoundary(); return;
    }
  }
  state.tempBoundary.push({ x: snapped.x, y: snapped.y });
  renderTemp();
}

function finishBoundary() {
  if (state.tempBoundary.length >= 3) { saveState(); state.boundary = [...state.tempBoundary]; }
  state.tempBoundary = [];
  autoAssignSprinklersToZones();
  renderAll();
}

function handleSprinklerClick(snapped: { x: number; y: number }) {
  saveState();
  const guided = snapToGuides(snapped, undefined);
  const id = state.nextSprinklerId++;
  state.sprinklers.push(createSprinkler({
    id,
    model: state.activeModel,
    x: guided.x,
    y: guided.y,
    arc: state.activeArc,
  }));
  autoAssignSprinklersToZones();
  selectSprinkler(id);
}

function handleZoneClick(snapped: { x: number; y: number }, e: MouseEvent) {
  if (e.detail === 2 && state.tempZone.length >= 3) { finishZone(); return; }
  state.tempZone.push({ x: snapped.x, y: snapped.y });
  renderTemp();
}

function finishZone() {
  if (state.tempZone.length >= 3) {
    saveState();
    const id = state.nextZoneId++;
    const colors = ["rgba(156,39,176,0.1)","rgba(3,169,244,0.1)","rgba(255,152,0,0.1)","rgba(233,30,99,0.1)"];
    state.zones.push({
      id, name: t("defaults.zoneName", { id }), points: [...state.tempZone],
      color: colors[(id - 1) % colors.length],
      valveModel: null,
    });
  }
  state.tempZone = [];
  autoAssignSprinklersToZones();
  renderAll();
}

function handlePipeClick(snapped: { x: number; y: number }, e: MouseEvent) {
  if (e.detail === 2 && state.tempPipe.length >= 2) { finishPipe(); return; }

  // Snap to nearby sprinkler or valve object
  const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
  const snap = findSnapTarget(snapped, snapThreshold);
  const pt = snap ? { x: snap.x, y: snap.y } : { x: snapped.x, y: snapped.y };

  state.tempPipe.push(pt);
  renderTemp();
}

function finishPipe() {
  if (state.tempPipe.length >= 2) {
    saveState();
    const id = state.nextPipeId++;
    const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
    const startSnap = findSnapTarget(state.tempPipe[0], snapThreshold);
    const endSnap = findSnapTarget(state.tempPipe[state.tempPipe.length - 1], snapThreshold);

    // Auto-detect zone from snapped sprinklers or valve objects
    let zoneId = null;
    if (startSnap) {
      if (startSnap.type === "sprinkler") {
        const sp = state.sprinklers.find((s) => s.id === startSnap.id);
        if (sp && sp.zoneId) zoneId = sp.zoneId;
      } else if (startSnap.type === "valveObj") {
        const vo = state.valveObjects.find((v) => v.id === startSnap.id);
        if (vo && vo.zoneId) zoneId = vo.zoneId;
      }
    }
    if (!zoneId && endSnap) {
      if (endSnap.type === "sprinkler") {
        const sp = state.sprinklers.find((s) => s.id === endSnap.id);
        if (sp && sp.zoneId) zoneId = sp.zoneId;
      } else if (endSnap.type === "valveObj") {
        const vo = state.valveObjects.find((v) => v.id === endSnap.id);
        if (vo && vo.zoneId) zoneId = vo.zoneId;
      }
    }

    state.pipes.push({
      id,
      name: t("defaults.pipeName", { id }),
      zoneId,
      diameter: state.activePipeDiameter,
      points: [...state.tempPipe],
      startSnap: startSnap ? { type: startSnap.type, id: startSnap.id } : null,
      endSnap: endSnap ? { type: endSnap.type, id: endSnap.id } : null,
    });
    state.selectedPipe = id;

    // Recompute auto-components for all pipes
    state.autoComponents = generateAutoComponents({
      pipes: state.pipes,
      // @ts-expect-error — TS strict
      sprinklers: state.sprinklers as unknown[],
      valveObjects: state.valveObjects,
    });
  }
  state.tempPipe = [];
  renderAll();
}

function handleDripClick(snapped: { x: number; y: number }, e: MouseEvent) {
  if (e.detail === 2 && state.tempDripLine.length >= 2) { finishDripLine(); return; }

  const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
  const snap = findSnapTarget(snapped, snapThreshold);
  const pt = snap ? { x: snap.x, y: snap.y } : { x: snapped.x, y: snapped.y };

  state.tempDripLine.push(pt);
  renderTemp();
}

function finishDripLine() {
  if (state.tempDripLine.length >= 2) {
    saveState();
    const id = state.nextDripLineId++;

    // Auto-detect zone from snapped elements
    const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
    const startSnap = findSnapTarget(state.tempDripLine[0], snapThreshold);
    let zoneId = null;
    if (startSnap && startSnap.type === "sprinkler") {
      const sp = state.sprinklers.find((s) => s.id === startSnap.id);
      if (sp && sp.zoneId) zoneId = sp.zoneId;
    }

    state.dripLines.push({
      id,
      name: t("defaults.dripName", { id }),
      model: state.activeDripModel,
      points: [...state.tempDripLine],
      zoneId,
    });
    state.selectedDripLine = id;
  }
  state.tempDripLine = [];
  renderAll();
}

function handleTreeClick(snapped: { x: number; y: number }) {
  saveState();
  const guided = snapToGuides(snapped, undefined);
  const id = state.nextTreeId++;
  const objType = ($("#activeObjectType") && $("#activeObjectType").value) || "tree";
  const def = LANDSCAPE_OBJECTS[objType] || LANDSCAPE_OBJECTS.tree;
  const shape = def.shape;

  const obj: StateTree = {
    id,
    name: t("defaults.treeName", { id }),
    type: objType,
    shape,
    x: guided.x,
    y: guided.y,
  };

  if (shape === 'rect') {
    obj.width = def.defaultWidth || 2;
    obj.length = def.defaultLength || 2;
    obj.rotation = 0;
  } else {
    obj.radius = def.defaultRadius || 1.5;
  }

  if (objType === 'waterSource') {
    obj.waterSourceProps = { ...WATER_SOURCE_DEFAULTS };
  }

  state.trees.push(obj);
  selectTree(id);
}

function handleValveObjClick(snapped: { x: number; y: number }) {
  saveState();
  const guided = snapToGuides(snapped, undefined);
  const id = state.nextValveObjId++;
  state.valveObjects.push({
    id, name: t("defaults.valveName", { id }), model: "PGV-101",
    x: guided.x, y: guided.y, zoneId: null,
  });
  selectValveObj(id);
}

function handleWeatherStationClick(snapped: { x: number; y: number }) {
  saveState();
  const guided = snapToGuides(snapped, undefined);
  const id = state.nextWeatherStationId++;
  const defaultModel = ((getWeatherStations() || []) as Record<string, unknown>[])[0]?.id as string || "ws-hunter-rain-clik";
  state.weatherStations.push({
    id, name: t("defaults.weatherStationName", { id }),
    model: defaultModel, x: guided.x, y: guided.y,
  });
  selectWeatherStation(id);
}

// flagged: `target` could be HTMLElement or SVGElement; `.closest()` returns Element | null
// which lacks `.dataset`. Proper fix: cast each `.closest()` result to HTMLElement. Track as follow-up.
function handleDeleteClick(target: any) {
  const marker = target.closest(".sprinkler-marker");
  if (marker) {
    saveState();
    const id = parseInt(marker.dataset.id);
    state.sprinklers = state.sprinklers.filter((s) => s.id !== id);
    if (state.selectedSprinkler === id) state.selectedSprinkler = null;
    renderAll(); return;
  }
  const bVertex = target.closest(".boundary-vertex");
  if (bVertex) {
    saveState(); state.boundary.splice(parseInt(bVertex.dataset.index), 1);
    renderAll(); return;
  }
  const zonePoly = target.closest(".zone-polygon");
  if (zonePoly) {
    saveState();
    const id = parseInt(zonePoly.dataset.zoneId);
    state.zones = state.zones.filter((z) => z.id !== id);
    if (state.selectedZone === id) state.selectedZone = null;
    autoAssignSprinklersToZones();
    renderAll(); return;
  }

  const dripLineEl = target.closest("[data-type='dripLine']");
  if (dripLineEl) {
    saveState();
    const id = parseInt(dripLineEl.dataset.id);
    state.dripLines = state.dripLines.filter((dl) => dl.id !== id);
    if (state.selectedDripLine === id) state.selectedDripLine = null;
    renderAll(); return;
  }
  const pipeLine = target.closest(".pipe-polyline");
  if (pipeLine) {
    saveState();
    const id = parseInt(pipeLine.dataset.id);
    state.pipes = state.pipes.filter((p) => p.id !== id);
    if (state.selectedPipe === id) state.selectedPipe = null;
    renderAll(); return;
  }
  // Delete tree
  const treeCrown = target.closest(".tree-crown");
  if (treeCrown) {
    saveState();
    const id = parseInt(treeCrown.dataset.id);
    state.trees = state.trees.filter((t) => t.id !== id);
    if (state.selectedTree === id) state.selectedTree = null;
    renderAll(); return;
  }
  // Delete valve object
  const valveMarker = target.closest(".valve-obj-marker");
  if (valveMarker) {
    saveState();
    const id = parseInt(valveMarker.dataset.id);
    state.valveObjects = state.valveObjects.filter((v) => v.id !== id);
    if (state.selectedValveObj === id) state.selectedValveObj = null;
    renderAll(); return;
  }
  // Delete weather station
  const wsMarker = target.closest(".weather-station-marker");
  if (wsMarker) {
    saveState();
    const id = parseInt(wsMarker.dataset.id);
    state.weatherStations = state.weatherStations.filter((w) => w.id !== id);
    if (state.selectedWeatherStation === id) state.selectedWeatherStation = null;
    renderAll(); return;
  }
}

// ── Drag Handler ────────────────────────────────────────────────────
function handleDrag(snapped: { x: number; y: number }) {
  // Any drag movement marks the gesture as a real mutation so mouseUp
  // commits the pre-drag snapshot to history.
  dragTargetMoved = true;

  // If the gesture started on a multi-selected element, translate every
  // selected point-like element by the cursor delta and re-render each
  // affected list. Preserves relative positions within the selection.
  if (applyMultiDrag(snapped)) {
    // Update pipe endpoints whose snap-anchor is a dragged sprinkler/valve.
    const spIds = new Set<number>();
    const voIds = new Set<number>();
    for (const e of multiDragEntries) {
      if (e.type === 'sprinkler') spIds.add(e.id);
      else if (e.type === 'valveObj') voIds.add(e.id);
    }
    if (spIds.size > 0 || voIds.size > 0) {
      state.pipes.forEach((pipe) => {
        if (pipe.startSnap && ((pipe.startSnap.type === 'sprinkler' && spIds.has(pipe.startSnap.id as number)) ||
                               (pipe.startSnap.type === 'valveObj' && voIds.has(pipe.startSnap.id as number)))) {
          const anchor = pipe.startSnap.type === 'sprinkler'
            ? state.sprinklers.find((s) => s.id === pipe.startSnap!.id)
            : state.valveObjects.find((v) => v.id === pipe.startSnap!.id);
          if (anchor) pipe.points[0] = { x: anchor.x, y: anchor.y };
        }
        if (pipe.endSnap && ((pipe.endSnap.type === 'sprinkler' && spIds.has(pipe.endSnap.id as number)) ||
                             (pipe.endSnap.type === 'valveObj' && voIds.has(pipe.endSnap.id as number)))) {
          const anchor = pipe.endSnap.type === 'sprinkler'
            ? state.sprinklers.find((s) => s.id === pipe.endSnap!.id)
            : state.valveObjects.find((v) => v.id === pipe.endSnap!.id);
          if (anchor) pipe.points[pipe.points.length - 1] = { x: anchor.x, y: anchor.y };
        }
      });
    }
    renderCoverage(); renderSprinklers(); renderTrees();
    renderValveObjects(); renderWeatherStations(); renderPipes();
    updateStats();
    return;
  }

  if (state.dragType === "sprinkler") {
    const sp = state.sprinklers.find((s) => s.id === state.dragTarget);
    if (sp) {
      const guided = snapToGuides(snapped, { type: "sprinkler", id: sp.id });
      sp.x = guided.x; sp.y = guided.y;
      renderSmartGuides(guided, { type: "sprinkler", id: sp.id });
      // Update snapped pipe endpoints. The snap type has to be checked as
      // well: sprinklers, valve objects and water sources each have their own
      // id counter, so matching on id alone dragged a pipe end anchored to
      // valve object #3 whenever sprinkler #3 moved. The valveObj branch
      // below has always checked the type.
      state.pipes.forEach((pipe) => {
        if (pipe.startSnap && pipe.startSnap.type === "sprinkler" && pipe.startSnap.id === sp.id) {
          pipe.points[0] = { x: sp.x, y: sp.y };
        }
        if (pipe.endSnap && pipe.endSnap.type === "sprinkler" && pipe.endSnap.id === sp.id) {
          pipe.points[pipe.points.length - 1] = { x: sp.x, y: sp.y };
        }
      });
      // No renderSprinklerList() here: nothing it shows changes during a drag,
      // and rebuilding it cost five DOM nodes per head on every mousemove. The
      // renderAll() on mouseup refreshes it once the zones are reassigned.
      renderCoverage(); renderPipes(); renderSprinklers();
      updatePropertiesPanel(); updateStats();
    }
  } else if (state.dragType === "boundary-vertex") {
    state.boundary[state.dragIndex!] = { x: snapped.x, y: snapped.y };
    renderBoundary(); updateStats();
  } else if (state.dragType === "zone-vertex") {
    const zone = state.zones.find((z) => z.id === state.dragTarget);
    if (zone) { zone.points[state.dragIndex!] = { x: snapped.x, y: snapped.y }; renderZones(); }
  } else if (state.dragType === "pipe-vertex") {
    const pipe = state.pipes.find((p) => p.id === state.dragTarget);
    if (pipe && pipe.points[state.dragIndex!]) {
      // Snap to sprinklers/valves for first and last vertex
      const isEndpoint = state.dragIndex === 0 || state.dragIndex === pipe.points.length - 1;
      const guided = snapToGuides(snapped, undefined);
      let pt = { x: guided.x, y: guided.y };
      if (isEndpoint) {
        const snapThreshold = Math.max(state.gridSize * 1.5, 0.3);
        const snap = findSnapTarget(guided, snapThreshold);
        if (snap) pt = { x: snap.x, y: snap.y };
        // Update snap references
        if (state.dragIndex === 0) {
          pipe.startSnap = snap ? { type: snap.type, id: snap.id } : null;
        } else {
          pipe.endSnap = snap ? { type: snap.type, id: snap.id } : null;
        }
      }
      pipe.points[state.dragIndex!] = pt;
      renderSmartGuides(pt, null);
      renderPipes(); updatePipePropertiesPanel();
    }
  } else if (state.dragType === "tree") {
    const tree = state.trees.find((t) => t.id === state.dragTarget);
    if (tree) {
      const guided = snapToGuides(snapped, { type: "tree", id: tree.id });
      tree.x = guided.x; tree.y = guided.y;
      renderSmartGuides(guided, { type: "tree", id: tree.id });
      renderTrees(); updateTreePropertiesPanel();
    }
  } else if (state.dragType === "valveObj") {
    const vo = state.valveObjects.find((v) => v.id === state.dragTarget);
    if (vo) {
      const guided = snapToGuides(snapped, { type: "valveObj", id: vo.id });
      vo.x = guided.x; vo.y = guided.y;
      renderSmartGuides(guided, { type: "valveObj", id: vo.id });
      // Update snapped pipe endpoints for valve objects
      state.pipes.forEach((pipe) => {
        if (pipe.startSnap && pipe.startSnap.type === "valveObj" && pipe.startSnap.id === vo.id) {
          pipe.points[0] = { x: vo.x, y: vo.y };
        }
        if (pipe.endSnap && pipe.endSnap.type === "valveObj" && pipe.endSnap.id === vo.id) {
          pipe.points[pipe.points.length - 1] = { x: vo.x, y: vo.y };
        }
      });
      renderValveObjects(); renderPipes(); updateValveObjPropertiesPanel();
    }
  } else if (state.dragType === "weatherStation") {
    const ws = state.weatherStations.find((w) => w.id === state.dragTarget);
    if (ws) {
      const guided = snapToGuides(snapped, { type: "weatherStation", id: ws.id });
      ws.x = guided.x; ws.y = guided.y;
      renderSmartGuides(guided, { type: "weatherStation", id: ws.id });
      renderWeatherStations(); updateWeatherStationPropertiesPanel();
    }
  } else if (state.dragType === "dripLine-vertex") {
    const dl = state.dripLines.find((d) => d.id === state.dragTarget);
    if (dl && dl.points[state.dragIndex!]) {
      const guided = snapToGuides(snapped, undefined);
      dl.points[state.dragIndex!] = { x: guided.x, y: guided.y };
      renderSmartGuides(guided, null);
      renderDripLines(); updateDripPropertiesPanel();
    }
  }
}

// ── Keyboard Shortcuts ─────────────────────────────────────────────
export function onKeyDown(e: KeyboardEvent) {
  // Keys typed in a dialog over the canvas belong to the dialog: its buttons
  // pass the form-field check below, so Delete removed the selection behind
  // it and Space, claimed for panning, never reached the focused button.
  if (e.target instanceof Element && e.target.closest('[role="dialog"], [role="alertdialog"]')) return;

  if (e.key === " " && !["INPUT", "SELECT", "TEXTAREA"].includes((e.target as HTMLElement)?.tagName)) {
    e.preventDefault();
    state.spaceHeld = true;
    // Spacebar pan-mode affordance, cleared on keyup in app.ts.
    container().classList.add("is-pannable");
    return;
  }

  if (["INPUT", "SELECT", "TEXTAREA"].includes((e.target as HTMLElement)?.tagName)) return;

  switch (e.key.toLowerCase()) {
    case "v":
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const before = elementCount();
        pasteClipboard({
          autoAssignSprinklersToZones, selectSprinkler, selectTree,
          selectValveObj, renderAll: callbacks.renderAll, t,
        });
        if (elementCount() > before) {
          showToast(t('toolbar.toastPasted'), 'success');
        }
      }
      else setMode("select");
      break;
    case "b": setMode("boundary"); break;
    case "s": setMode("sprinkler"); break;
    case "z":
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) { e.preventDefault(); redo(callbacks.renderAll); }
      else if (e.ctrlKey || e.metaKey) { e.preventDefault(); undo(callbacks.renderAll); }
      else setMode("zone");
      break;
    case "y":
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); redo(callbacks.renderAll); }
      break;
    case "c":
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        copySelected();
        if (state.clipboard) {
          showToast(t('toolbar.toastCopied'), 'success');
        }
      }
      break;
    case "delete": case "backspace":
      // If multi-selection is active, batch-delete them first
      if (state.multiSelection.size > 0) {
        const deleted = deleteMultiSelection();
        if (deleted > 0) {
          showToast(t('toolbar.toastBulkDeleted').replace('{{n}}', String(deleted)), 'success');
          autoAssignSprinklersToZones();
          renderAll();
        }
        break;
      }
      if (state.selectedSprinkler) {
        saveState();
        state.sprinklers = state.sprinklers.filter((s) => s.id !== state.selectedSprinkler);
        state.selectedSprinkler = null; renderAll();
      } else if (state.selectedDripLine) {
        saveState();
        state.dripLines = state.dripLines.filter((dl) => dl.id !== state.selectedDripLine);
        state.selectedDripLine = null; renderAll();
      } else if (state.selectedPipe) {
        saveState();
        state.pipes = state.pipes.filter((p) => p.id !== state.selectedPipe);
        state.selectedPipe = null; renderAll();
      } else if (state.selectedTree) {
        saveState();
        state.trees = state.trees.filter((t) => t.id !== state.selectedTree);
        state.selectedTree = null; renderAll();
      } else if (state.selectedValveObj) {
        saveState();
        state.valveObjects = state.valveObjects.filter((v) => v.id !== state.selectedValveObj);
        state.selectedValveObj = null; renderAll();
      } else if (state.selectedZone) {
        saveState();
        state.zones = state.zones.filter((z) => z.id !== state.selectedZone);
        state.selectedZone = null;
        autoAssignSprinklersToZones();
        renderAll();
      }
      break;
    case "t": setMode("tree"); break;
    case "p": setMode("pipe"); break;
    case "k": setMode("valveObj"); break;
    case "w": setMode("weatherStation"); break;
    case "d":
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const beforeDup = elementCount();
        copySelected();
        pasteClipboard({
          autoAssignSprinklersToZones, selectSprinkler, selectTree,
          selectValveObj, renderAll: callbacks.renderAll, t,
        });
        if (elementCount() > beforeDup) {
          showToast(t('toolbar.toastDuplicated'), 'success');
        }
      }
      else setMode("delete");
      break;
    case "i": setMode("drip"); break;
    case "escape":
      state.tempBoundary = []; state.tempZone = []; state.tempPipe = []; state.tempDripLine = [];
      clearMultiSelection();
      clearSelection();
      setMode("select"); renderAll(); break;
    case "f": zoomToFit(); break;
    case "+": case "=": zoomAt(container().clientWidth / 2, container().clientHeight / 2, 0.9); break;
    case "-": zoomAt(container().clientWidth / 2, container().clientHeight / 2, 1.1); break;
  }
}
